import React, { useState, useEffect } from "react";
import Header from "../../../components/Header";
import SEO from "../../../components/SEO";
import Banner from "../../../components/Banner";
import Footer from "../../../components/Footer";
import ScrollToTopButton from "../../../components/ScrollTop";
import WhatsAppMsg from "../../../components/WhatsApp";
import ProductSpecifications from "../../../components/ProductSpecification";
import inertiaLEDProductSpecs from "../../../utils/inertiaSpecs";
import { Helmet } from "react-helmet";
// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
// import required modules
import { Navigation } from "swiper/modules";

const slides = [
  {
    id: 1,
    title: "Entertainment & Events",
    altText:
      "COLTEC Inertia LED Video Wall for Entertainment & Events – Dynamic visual experience for concerts & live shows.",
    description:
      "Concerts, sports arenas, and live events leverage LED Video Walls for immersive audience experiences. Whether displaying live feeds, dynamic backgrounds, or interactive elements, these walls create a visually stunning impact, enhancing engagement and entertainment.",
    imageUrl:
      "https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-entertainment-events.webp",
  },
  {
    id: 2,
    title: "Retail & Shopping Malls",
    altText:
      "COLTEC Inertia LED Video Wall for Shopping Malls – Advanced advertising and promotional displays.",
    description:
      "Retail stores and malls deploy LED Video Walls to attract customers with engaging advertisements, promotions, and digital signage. The vibrant visuals captivate shoppers, driving higher foot traffic and increasing brand engagement. Interactive touch-enabled video walls also enhance the shopping experience.",
    imageUrl:
      "https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-shopping-mall.webp",
  },
  {
    id: 3,
    title: "Control Rooms & Command Centers",
    altText:
      "COLTEC Inertia LED Video Wall in Control Rooms – Real-time data visualization for mission-critical monitoring.",
    description:
      "Security operations, traffic control, and network monitoring centers rely on LED Video Walls for real-time surveillance and data tracking. These large-scale displays provide an uninterrupted flow of information, enabling faster response times in critical situations.",
    imageUrl:
      "https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-control-room.webp",
  },
  {
    id: 4,
    title: "Corporate Boardrooms & Conference Rooms",
    altText:
      "COLTEC Inertia LED Video Wall in Boardrooms – Enhancing corporate presentations & meetings.",
    description:
      "Large corporations utilize LED Video Walls for high-impact presentations, real-time data visualization, and seamless video conferencing. These displays enhance collaboration, ensuring clear visibility for all participants. With advanced integration options, executives can make data-driven decisions efficiently.",
    imageUrl:
      "https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-boardroom.webp",
  },
  {
    id: 5,
    title: "Education & Training Centers",
    altText:
      "COLTEC Inertia LED Video Wall in Education & Training Centers – Interactive learning and digital teaching solutions.",
    description:
      "Educational institutions and corporate training centers use LED Video Walls to deliver interactive learning experiences. From virtual labs to immersive storytelling, these high-definition displays make learning more engaging, ensuring better retention of information.",
    imageUrl:
      "https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-education-training.webp",
  },
];

const relatedCases = [
  {
    id: 1,
    imageUrl:
      "https://content.qeeb.in/coltec/products/active-led/coltec-active-led-video-wall-reception-area.webp",
    altText:
      "LED Video Wall Use Case in Corporate Reception – Dynamic branding and engaging digital signage.",
    buttonText: " Reception Area",
  },
  {
    id: 2,
    imageUrl:
      "https://content.qeeb.in/coltec/products/active-led/coltec-active-led-video-wall-news-bord.webp",
    altText:
      "LED Video Wall Use Case in News Room – Real-time broadcast visuals and live updates.",
    buttonText: "News Room",
  },
  {
    id: 3,
    imageUrl:
      "https://content.qeeb.in/coltec/products/active-led/coltec-active-led-video-wall-board-room.webp",
    altText:
      "LED Video Wall Use Case in Boardroom – Enhancing corporate presentations and discussions.",
    buttonText: "Board Room",
  },
  {
    id: 4,
    altText:
      "COLTEC Inertia LED Video Wall for Shopping Malls – Advanced advertising and promotional displays.",
    imageUrl:
      "https://content.qeeb.in/coltec/products/active-led/active-led-malls.webp",
    buttonText: "Malls",
  },
];

const Inertia = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const goToSlide = (slideIndex) => {
    setCurrentIndex(slideIndex);
  };
  // Automatically change slide every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <div className="bg-[radial-gradient(rgba(18,51,77,1)_0%,_rgba(2,8,15,1)_100%)]">
        <SEO
          title="COLTEC INERTIA - LED Video Wall | Stunning Visuals & Seamless Display Solutions"
          description="Experience breathtaking visuals with COLTEC INERTIA LED Video Wall. Seamless, high-resolution display solutions for boardrooms, retail spaces, corporate lobbies, control rooms, and large venues. Elevate your space with vibrant colors and ultra-clear performance."
          ogTitle="COLTEC INERTIA - LED Video Wall | Stunning Visuals & Seamless Display Solutions"
          keywords="LED Video Wall,Indoor LED Video Wall,Seamless LED Video Wall,High-Resolution LED Video Wall,Commercial LED Video Wall,LED Display Panel,LED Video Wall for Events,LED Screen Display"
          ogDescription="Experience breathtaking visuals with COLTEC INERTIA LED Video Wall. Seamless, high-resolution display solutions for boardrooms, retail spaces, corporate lobbies, control rooms, and large venues. Elevate your space with vibrant colors and ultra-clear performance."
          ogImage="https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall.webp"
          ogUrl="https://coltec-global.com/products/active-led/inertia-series"
          canonicalUrl="https://coltec-global.com/products/active-led/inertia-series"
          ogImageAlt="COLTEC Inertia LED Video Wall showcasing an ultra-high resolution display with seamless, bezel-free clarity and vibrant visuals, ideal for boardrooms, retail, and immersive environments"
          ogSiteName="Coltec Global"
          ogType="website"
          twitterSite="https://x.com/ColtecGlobal" // Twitter username associated with the site
          twitterTitle="COLTEC INERTIA - LED Video Wall | Stunning Visuals & Seamless Display Solutions"
          twitterDescription="Experience breathtaking visuals with COLTEC INERTIA LED Video Wall. Seamless, high-resolution display solutions for boardrooms, retail spaces, corporate lobbies, control rooms, and large venues. Elevate your space with vibrant colors and ultra-clear performance."
          twitterImage="https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall.webp"
          twitterCard="summary" // Using a large image card for enhanced visual display
          geoRegion="IN"
        />
        {/* JSON‑LD Structured Data with Publisher & Address Details */}
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(
              {
                "@context": "https://schema.org",
                "@graph": [
                  // Organization details
                  {
                    "@type": "Organization",
                    "@id": "https://coltec-global.com/#organization",
                    name: "COLTEC Global",
                    aggregateRating: {
                      "@type": "AggregateRating",
                      ratingValue: "4.8",
                      reviewCount: "350",
                    },
                    description:
                      "COLTEC Global is a leading manufacturer of Visual Interactive Display Technology, delivering innovative solutions for Education, Corporate, and Retail applications.",
                    url: "https://coltec-global.com",
                    logo: {
                      "@type": "ImageObject",
                      url: "https://content.qeeb.in/coltec/coltec-logo-black.svg",
                    },
                    address: [
                      {
                        "@type": "PostalAddress",
                        streetAddress:
                          "37, Survey No 75/A, Gagan Pahad Village, Raghavendra Nagar",
                        addressLocality: "Hyderabad",
                        addressRegion: "Telangana",
                        postalCode: "500030",
                        addressCountry: "IN",
                      },
                      {
                        "@type": "PostalAddress",
                        streetAddress:
                          "COLTEC GLOBAL #41-14-8/2, Opp Mouli Towers, Near Jyothi Convention Hall,Benz circle",
                        addressLocality: "Vijayawada",
                        addressRegion: "Andhra Pradesh",
                        postalCode: "520 010",
                        addressCountry: "IN",
                      },
                    ],
                    sameAs: [
                      "https://www.facebook.com/profile.php?id=61570213181020",
                      "https://x.com/ColtecGlobal",
                      "https://www.linkedin.com/company/coltec-global/",
                      "https://www.youtube.com/@coltecglobal5339",
                      "https://www.google.com/maps/place/COLTEC+GLOBAL+INDIA/@17.3297791,78.4381149,17z/data=!3m1!4b1!4m6!3m5!1s0x3bcbbd8bb97fefcd:0x2536a4697f2f1423!8m2!3d17.3297791!4d78.4406898!16s%2Fg%2F11w1dth9rd?entry=ttu&g_ep=EgoyMDI1MDIyNi4xIKXMDSoJLDEwMjExNDUzSAFQAw%3D%3D",
                    ],
                    email: "sales@coltec-global.com",
                    contactPoint: [
                      {
                        "@type": "ContactPoint",
                        telephone: "+91 1800 309 8822",
                        contactType: "customer support",
                      },
                    ],
                  },
                  // WebSite details
                  {
                    "@type": "WebSite",
                    "@id": "https://coltec-global.com/#website",
                    url: "https://coltec-global.com",
                    name: "COLTEC Global",
                    publisher: {
                      "@id": "https://coltec-global.com/#organization",
                    },
                    potentialAction: {
                      "@type": "SearchAction",
                      target:
                        "https://coltec-global.com/?s={search_term_string}",
                      "query-input": "required name=search_term_string",
                    },
                    inLanguage: "en",
                  },
                  {
                    "@type": "Product",
                    name: "COLTEC Inertia Active LED Video Wall",
                    url: "https://coltec-global.com/products/active-led/inertia-series",
                    description:
                      "Experience brighter, more intuitive teamwork and learning with Coltec’s Inspire Series Interactive Flat Panels—designed to spark creativity and results.",
                    brand: {
                      "@type": "Brand",
                      name: "COLTEC Inertia Series",
                      url: "https://coltec-global.com/products/active-led/inertia-series",
                      image:
                        "https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall.webp",
                    },
                    offers: {
                      "@type": "Offer",
                      offerCount: 5,
                      lowPrice: "10,80,000",
                      highPrice: "36,00,000",
                      priceCurrency: "INR",
                      availability: "https://schema.org/InStock",
                    },
                    review: [
                      {
                        "@type": "Review",
                        headline: "COLTEC Inertia Active LED Video Wall",
                        itemReviewed: {
                          "@type": "Product",
                          name: "A Must-Have for Modern Commercial Spaces",
                        },
                        reviewRating: {
                          "@type": "Rating",
                          ratingValue: 4,
                          bestRating: 5,
                        },
                        author: {
                          "@type": "Person",
                          name: "Ashnaaf Mahfooz",
                        },

                        datePublished: "2024-02-10T12:00:00+05:30",
                      },
                      {
                        "@type": "Review",
                        headline: "Inertia Active LED",
                        itemReviewed: {
                          "@type": "Product",
                          name: "Bright, Engaging Screen That Captures Attention",
                        },
                        reviewRating: {
                          "@type": "Rating",
                          ratingValue: 4,
                          bestRating: 5,
                        },
                        author: {
                          "@type": "Person",
                          name: "Mohammad Salman",
                        },
                        datePublished: "2024-01-15T10:30:00+05:30",
                      },
                    ],
                    image: [
                      {
                        "@type": "ImageObject",
                        name: "Entertainment & Events",
                        url: "https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-entertainment-events.webp",
                        description:
                          "Concerts, sports arenas, and live events leverage LED Video Walls for immersive audience experiences. Whether displaying live feeds, dynamic backgrounds, or interactive elements, these walls create a visually stunning impact, enhancing engagement and entertainment.",
                      },
                      {
                        "@type": "ImageObject",
                        name: "Retail & Shopping Malls",
                        url: "https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-shopping-mall.webp",
                        description:
                          "Retail stores and malls deploy LED Video Walls to attract customers with engaging advertisements, promotions, and digital signage. The vibrant visuals captivate shoppers, driving higher foot traffic and increasing brand engagement. Interactive touch-enabled video walls also enhance the shopping experience.",
                      },
                      {
                        "@type": "ImageObject",
                        name: "Control Rooms & Command Centers",
                        url: "https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-control-room.webp",
                        description:
                          "Security operations, traffic control, and network monitoring centers rely on LED Video Walls for real-time surveillance and data tracking. These large-scale displays provide an uninterrupted flow of information, enabling faster response times in critical situations.",
                      },
                      {
                        "@type": "ImageObject",
                        name: "Corporate Boardrooms & Conference Rooms",
                        url: "https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-boardroom.webp",
                        description:
                          "Large corporations utilize LED Video Walls for high-impact presentations, real-time data visualization, and seamless video conferencing. These displays enhance collaboration, ensuring clear visibility for all participants. With advanced integration options, executives can make data-driven decisions efficiently.",
                      },
                      {
                        "@type": "ImageObject",
                        name: "Education & Training Centers",
                        url: "https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-education-training.webp",
                        description:
                          "Educational institutions and corporate training centers use LED Video Walls to deliver interactive learning experiences. From virtual labs to immersive storytelling, these high-definition displays make learning more engaging, ensuring better retention of information.",
                      },
                    ],
                  },
                  // Breadcrumb for Inspire Series page
                  {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                      {
                        "@type": "ListItem",
                        position: 1,
                        name: "Home",
                        item: "https://coltec-global.com",
                      },
                      {
                        "@type": "ListItem",
                        position: 2,
                        name: "Coltec Inertia Series",
                        item: "https://coltec-global.com/products/active-led/inertia-series",
                      },
                    ],
                  },
                  {
                    "@type": "LocalBusiness",
                    "@id": "https://coltec-global.com/#localbusiness",
                    name: "COLTEC Global",
                    url: "https://coltec-global.com",
                    image:
                      "https://content.qeeb.in/coltec/coltec-logo-black.svg",
                    address: {
                      "@type": "PostalAddress",
                      streetAddress:
                        "37, Survey No 75/A, Gagan Pahad Village, Raghavendra Nagar",
                      addressLocality: "Hyderabad",
                      addressRegion: "Telangana",
                      postalCode: "500030",
                      addressCountry: "IN",
                    },
                    telephone: "+91 1800 309 8822",
                    openingHours: "Mon-Sat 09:00-6:00",
                    geo: {
                      "@type": "GeoCoordinates",
                      latitude: "17.3297791",
                      longitude: "78.4381149",
                    },
                    sameAs: [
                      "https://www.facebook.com/profile.php?id=61570213181020",
                      "https://x.com/ColtecGlobal",
                      "https://www.linkedin.com/company/coltec-global/",
                      "https://www.youtube.com/@coltecglobal5339",
                      "https://www.google.com/maps/place/COLTEC+GLOBAL+INDIA/@17.3297791,78.4381149,17z/data=!3m1!4b1!4m6!3m5!1s0x3bcbbd8bb97fefcd:0x2536a4697f2f1423!8m2!3d17.3297791!4d78.4406898!16s%2Fg%2F11w1dth9rd?entry=ttu&g_ep=EgoyMDI1MDIyNi4xIKXMDSoJLDEwMjExNDUzSAFQAw%3D%3D",
                    ],
                  },
                ],
              },
              null,
              2
            )}
          </script>
        </Helmet>
        <Header />
        <div className="relative">
          <Banner
            imgSrc="https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall.webp"
            mobileImgSrc="https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-mobile.webp"
            altText="COLTEC Inertia LED Video Wall Series – High-performance LED display for professional applications."
            headerText="INERTIA SERIES"
            bannerHeight="md:h-[800px] h-[300px]"
            imgHeight="h-[300px] lg:h-auto w-full"
            heading="LED Video Wall Series - Where Innovation Meets Stunning Visual Brilliance"
            headClasses="hidden md:block max-w-md text-[18px] font-semibold tracking-tight leading-6 text-white md:text-xl drop-shadow-lg md:mt-3"
            opacityClasses="opacity-layer absolute inset-0 w-full h-full bg-neutral-900/50 md:bg-neutral-900/20 z-0"
            positionClasses="absolute md:top-[40%] bottom-10 left-0 lg:left-0 lg:right-0 text-center"
            headerTextClasses="hidden md:block xl:text-[50px] xl:max-w-[400px] md:text-[44px] text-[24px] font-bold not-italic text-[#31F5E4] leading-1 md:leading-[65px]"
            paragraphTextClasses="hidden md:block md:text-[13px] text-[11px] not-italic font-normal mt-1 md:mt-4 text-white max-w-[410px]"
            paragraphText="Step into the future of digital display technology with the COLTEC Inertia LED Video Wall—a perfect blend of cutting-edge engineering and breathtaking aesthetics. Designed for seamless, bezel-free clarity, it delivers ultra-high resolution, rich contrast, and vibrant colors that transform any space into a visual masterpiece."
            paragraphTextThree="With unmatched brightness, superior durability, and limitless scalability, COLTEC Inertia ensures every frame is a work of art, captivating audiences in any environment."
            paragraphTextThreeClasses="hidden md:block md:max-w-[410px] md:text-[13px] text-[11px] font-normal text-white"
          />
          <div className="md:hidden block px-6 py-8 bg-white">
            <div className="bg-neutral-900/60 absolute bottom-[285px] left-0 right-0 px-5 py-4">
              <h2 className="relative justify-start text-white mb-2 text-[36px] font-bold uppercase leading-[34.56px]">
                INERTIA SERIES
              </h2>
              <p className="relative justify-start text-white text-base font-medium font-['Poppins'] leading-snug">
                Where Innovation Meets Stunning Visual Brilliance
              </p>
            </div>
            <p className="max-w-[373px] relative justify-start text-black text-sm mb-3 font-normal font-['Poppins'] leading-[19px]">
              Step into the future of digital display technology with the COLTEC
              Inertia LED Video Wall—a perfect blend of cutting-edge engineering
              and breathtaking aesthetics. Designed for seamless, bezel-free
              clarity, it delivers ultra-high resolution, rich contrast, and
              vibrant colors that transform any space into a visual masterpiece.
            </p>
            <p className="max-w-[373px] relative justify-start text-black text-sm font-normal font-['Poppins'] leading-[19px]">
              With unmatched brightness, superior durability, and limitless
              scalability, COLTEC Inertia ensures every frame is a work of art,
              captivating audiences in any environment.
            </p>
          </div>
        </div>

        <div>
          <img
            className="lg:h-[550px] w-full"
            alt="COLTEC Inertia LED Video Wall in a Museum – Enhancing visitor engagement with immersive visuals."
            src="https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-museum-angle.webp"
          />
          <div
            className="pb-20 pt-8"
            data-aos="fade-up"
            data-aos-duration="500"
            data-aos-delay="300"
          >
            <div className="lg:w-[567px] px-5 md:px-0 mx-auto md:text-center">
              <h2 class="text-white md:text-[28px] text-[22px] font-bold font-['Poppins'] leading-[33.60px]">
                Ultra-High Resolution{" "}
                <span className="font-medium md:hidden">
                  for Maximum Impact
                </span>
              </h2>
              <span class="text-white md:block hidden md:text-[28px] text-[22px] font-medium font-['Poppins'] leading-[33.60px]">
                for Any Environment
              </span>
            </div>
            <div className="md:w-[690px] px-5 md:px-0 mx-auto md:text-center md:pt-6 pt-4">
              <h3 class="text-white text-sm font-normal font-['Poppins'] leading-normal">
                COLTEC commercial LED Video Wall delivers{" "}
                <span className="font-semibold">
                  Full HD, 4K, and even 8K clarity
                </span>
                , ensuring{" "}
                <span className="font-semibold">
                  stunning visuals, vibrant colors, and crisp text.
                </span>{" "}
                Whether for{" "}
                <span className="font-semibold">
                  retail displays, corporate presentations, or digital signage
                </span>
                , high resolution enhances visibility and engagement.
              </h3>
            </div>
          </div>
        </div>

        <div>
          <img
            className="lg:h-[500px] object-cover w-full"
            alt="COLTEC Inertia LED Video Wall in Showrooms – Premium digital signage for showcasing products."
            src="https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-showrooms.webp"
          />
          <div
            className="pb-20 pt-8"
            data-aos="fade-up"
            data-aos-duration="500"
            data-aos-delay="300"
          >
            <div className="lg:w-[567px] mx-auto px-5 md:px-0 md:text-center">
              <h2 class="text-white md:text-[28px] text-[22px] font-bold font-['Poppins'] leading-[33.60px]">
                Seamless, Bezel-Free Design{" "}
              </h2>
              <span class="text-white md:text-[28px] text-[22px] font-medium font-['Poppins'] leading-[33.60px]">
                for a Premium Look
              </span>
            </div>
            <div className="md:w-[700px] mx-auto md:text-center px-5 md:px-0 md:pt-6 pt-4">
              <h3 class="text-white text-sm font-normal font-['Poppins'] leading-normal">
                Unlike traditional LCD screens, an LED Video Wall features a
                bezel-free design, creating a continuous, uninterrupted display.
                This ensures a sleek, professional appearance—perfect for
                conference rooms, control centers, shopping malls, and
                entertainment venues.
              </h3>
            </div>
          </div>
        </div>

        <div>
          <img
            className="lg:h-[500px] w-full object-center"
            alt="COLTEC Inertia LED Video Wall for Retail – Captivating shoppers with high-quality digital displays."
            src="https://content.qeeb.in/coltec/products/active-led/coltec-inertia-led-video-wall-retail-mall.webp"
          />
          <div
            className="pb-14 pt-8"
            data-aos="fade-up"
            data-aos-duration="500"
            data-aos-delay="300"
          >
            <div className="lg:w-[567px] mx-auto px-5 md:px-0 lg:text-center">
              <h2 class="text-white md:text-[28px] text-[22px] font-bold font-['Poppins'] leading-[33.60px]">
                High Brightness & Wide Viewing Angles{" "}
                <span className="font-medium md:hidden">
                  for Any Environment
                </span>
              </h2>
              <span class="text-white md:text-[28px] hidden md:block text-[22px] font-medium font-['Poppins'] leading-[33.60px]">
                for Any Environment
              </span>
            </div>
            <div className="md:w-[700px] mx-auto md:text-center px-5 md:px-0 pt-4 md:pt-6">
              <h3 class="text-white text-sm font-normal font-['Poppins'] leading-normal">
                With brightness levels up to 10,000 nits and 160° viewing
                angles, an LED Video Wall remains clearly visible in any
                lighting condition—even in bright outdoor spaces or large indoor
                venues. This makes it an ideal solution for advertising,
                exhibitions, and command centers.
              </h3>
            </div>
          </div>
        </div>

        <div className="md:pt-10 md:pb-16 pb-8">
          <div className="px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="md:text-center">
              <span class="text-white lg:text-4xl text-2xl font-semibold font-['Poppins'] md:leading-[57.60px]">
                Application{" "}
              </span>
              <span class="text-scenarios lg:text-4xl text-2xl font-semibold font-['Poppins'] md:leading-[57.60px]">
                Scenarios
              </span>
            </div>
            <div className="w-full md:pt-10 pt-8">
              {/* Slider Container */}
              <div className="relative overflow-hidden md:h-80 h-[580px]">
                {slides.map((slide, index) => (
                  <div
                    key={slide.id}
                    className={`
              absolute inset-0 transition-all duration-700 
              ${index === currentIndex ? "opacity-100" : "opacity-0"}
            `}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-16 gap-4">
                      <div>
                        <img
                          src={slide.imageUrl}
                          alt={slide.altText}
                          className="w-full md:h-[300px] object-cover rounded-lg"
                        />
                      </div>
                      <div className="flex flex-col justify-between">
                        <div>
                          <h2 className="md:text-2xl text-xl text-white md:mb-8 mb-4 font-semibold">
                            {slide.title}
                          </h2>
                          <p className="md:text-[15px] text-sm leading-6 text-white font-normal">
                            {slide.description}
                          </p>
                        </div>
                        {/* Custom Pagination with Outer Circle and Connecting Line */}
                        <div className="flex items-center mt-5 md:mt-0">
                          {slides.map((_, index) => (
                            <React.Fragment key={index}>
                              <button
                                onClick={() => goToSlide(index)}
                                className="flex flex-col items-center rounded-full focus:outline-none"
                              >
                                {/* Outer circle */}
                                <div
                                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ${
                                    index === currentIndex
                                      ? "border-linergrd"
                                      : "border-gray-300"
                                  }`}
                                >
                                  {/* Inner circle if active */}
                                  {index === currentIndex && (
                                    <div className="w-2 h-2 rounded-full gradient-background"></div>
                                  )}
                                </div>
                              </button>
                              {/* Connecting line between dots (except after the last dot) */}
                              {index !== slides.length - 1 && (
                                <div className="w-14 h-0.5 bg-gray-300 mx-2 rounded-xl"></div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <img
            alt="Crystal Clear Large LED Video Wall – High-resolution display for impactful visuals."
            src="https://content.qeeb.in/coltec/products/active-led/crystal-clear-large-led-video-wall.gif"
            className="h-[170px] md:h-auto"
          />
          <p className="pt-8 mx-auto px-5 lg:px-0 lg:w-[860px] pb-6 md:text-center text-white text-sm md:text-base font-normal font-['Poppins'] leading-normal">
            Experience seamless, high-definition displays with ultra-bright
            clarity, superior color accuracy, and cutting-edge technology
            designed for corporate, retail, and large-scale event applications.
            COLTEC Inertia LED Video Wall redefines immersive viewing with
            precision, durability, and unparalleled performance.
          </p>
          <div className="md:text-center px-5 lg:px-0">
            <span class="text-white md:text-2xl text-xl font-medium font-['Poppins'] leading-9">
              Seamless Display, Maximum Impact –{" "}
            </span>
            <span class="text-white md:text-2xl text-xl font-bold font-['Poppins'] leading-9">
              Customize Your LED Video Wall!
            </span>
          </div>
          <div className="text-center flex justify-center md:pt-6 md:pb-8 py-8">
            <button
              onClick={openModal}
              className="pr-5 pl-5 flex justify-center items-center relative text-base font-semibold h-11 bg-white rounded-[22px] border"
            >
              Request Consultation
              <img
                alt="arrow-right"
                src="https://content.qeeb.in/coltec/chevron-right.svg"
                class="chevron-right lg:inline-block ml-4 w-[18px]"
              ></img>
            </button>
          </div>
        </div>

        <div className="md:py-10 py-8">
          <div className="px-5 lg:px-8 max-w-7xl mx-auto">
            <div className="md:w-[755px] mx-auto md:text-center mb-8">
              <span class="text-white lg:text-4xl text-xl font-semibold font-['Poppins'] lg:leading-[57.60px]">
                COLTEC{" "}
              </span>
              <span class="text-[#6dc7ff] lg:text-4xl text-xl font-semibold font-['Poppins'] lg:leading-[57.60px]">
                Inertia LED Video Wall
              </span>
              <span class="text-white lg:text-4xl text-xl font-semibold font-['Poppins'] lg:leading-[57.60px]">
                {" "}
                – Precision, Performance & Customization
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-16">
              <div>
                <p className="md:w-[507px] text-white text-sm font-normal font-['Poppins'] leading-normal mb-6">
                  The COLTEC Inertia LED Video Wall series simplifies
                  installation while delivering exceptional 4K and 8K
                  ultra-high-definition visuals. Select the ideal screen size
                  for your application and receive a fully integrated,
                  all-in-one solution—engineered for efficiency, reliability,
                  and stunning visual performance. Whether for corporate
                  environments, retail spaces, or large-scale events, COLTEC
                  Inertia LED Video Wall ensures seamless, high-impact display
                  experiences with effortless deployment.
                </p>
                <h4 className="text-white text-base font-semibold font-['Poppins'] leading-[28.80px] mb-2">
                  Included
                </h4>
                <ul className="grid grid-cols-2 list-disc gap-2 pl-4">
                  <li className="text-sm text-white">LED Display Cabinet</li>
                  <li className="text-sm text-white">Wall Mounts</li>
                  <li className="text-sm text-white">Video Controller</li>
                  <li className="text-sm text-white">Cables</li>
                  <li className="text-sm text-white">Remote Power Supply</li>
                  <li className="text-sm text-white">Spare Modules</li>
                </ul>
                <div className="grid md:grid-cols-2 grid-cols-1">
                  <div className="mt-6">
                    <h4 className="text-white text-base font-semibold font-['Poppins'] leading-[28.80px] mb-2">
                      Available Sizes
                    </h4>
                    <div className="text-white text-sm font-medium font-['Poppins'] leading-normal">
                      108” | 136” | 163” | 217”
                    </div>
                  </div>
                  <div className="mt-6">
                    <h4 className="text-white text-base font-semibold font-['Poppins'] leading-[28.80px] mb-2">
                      Pixel Pitch
                    </h4>
                    <div className="text-white text-sm font-medium font-['Poppins'] leading-normal">
                      0.93MM | 1.25MM | 1.56MM
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img
                  className="absolute lg:left-[-300px] lg:top-[-50px] z-1"
                  alt="shadow-video-wall"
                  src="https://content.qeeb.in/coltec/products/active-led/shadow-video-wall.webp"
                />
                <img
                  alt="LED Video Wall Customizable in Multiple Sizes – Tailored solutions for different spaces."
                  src="https://content.qeeb.in/coltec/products/active-led/led-video-wall-customizable-sizes.webp"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="md:py-10">
          <div className="px-6 lg:px-8 max-w-7xl mx-auto text-white">
            <div>
              <h4 className="md:text-center md:mb-10 mb-5">
                <span class="text-white md:text-4xl text-2xl font-semibold font-['Poppins'] leading-[57.60px]">
                  Key{" "}
                </span>
                <span class="text-scenarios md:text-4xl text-2xl font-semibold font-['Poppins'] leading-[57.60px]">
                  Features
                </span>
              </h4>
              <div className="md:flex md:justify-center md:flex-row grid grid-cols-2 gap-6">
                <div className="md:flex md:pr-10">
                  <div className="flex items-center flex-col md:h-[60px] justify-between md:pr-10">
                    <div>
                      <img
                        alt="HDR Technology"
                        src="https://content.qeeb.in/coltec/products/active-led/hdr-technology.svg"
                        className="h-4 w-auto md:mb-0 mb-2"
                      />
                    </div>
                    <p className="text-sm font-medium">HDR Technology</p>
                  </div>
                  <div className="md:block hidden">
                    <img
                      alt="line-feature"
                      className="h-[60px]"
                      src="https://content.qeeb.in/coltec/products/active-led/line-feature-border.svg"
                    />
                  </div>
                </div>
                <div className="md:flex md:pr-10">
                  <div className="flex items-center flex-col md:h-[60px] justify-between md:pr-10">
                    <div>
                      <img
                        alt="Front Access"
                        src="https://content.qeeb.in/coltec/products/active-led/front-access.svg"
                        className="h-5 md:h-6 md:mb-0 mb-2 w-auto"
                      />
                    </div>
                    <p className="text-sm font-medium">Front Access</p>
                  </div>
                  <div className="md:block hidden">
                    <img
                      alt="line-feature"
                      className="h-[60px]"
                      src="https://content.qeeb.in/coltec/products/active-led/line-feature-border.svg"
                    />
                  </div>
                </div>
                <div className="md:flex md:pr-10">
                  <div className="flex items-center flex-col md:h-[60px] justify-between md:pr-10">
                    <div>
                      <img
                        alt="16:9 Cabinet Aspect Ratio"
                        src="https://content.qeeb.in/coltec/products/active-led/cabinet-aspect-ratio.svg"
                        className="h-5 md:h-6 mb-2 md:mb-0 w-auto"
                      />
                    </div>
                    <p className="text-sm text-center font-medium">
                      16:9 Cabinet Aspect Ratio
                    </p>
                  </div>
                  <div className="hidden md:block">
                    <img
                      alt="line-feature"
                      className="h-[60px]"
                      src="https://content.qeeb.in/coltec/products/active-led/line-feature-border.svg"
                    />
                  </div>
                </div>
                <div className="md:flex">
                  <div className="flex items-center flex-col md:h-[60px] justify-between">
                    <div>
                      <img
                        alt="Integrated Module Design"
                        src="https://content.qeeb.in/coltec/products/active-led/integrated-module-design.svg"
                        className="h-5 md:h-6 mb-2 md:mb-0 w-auto"
                      />
                    </div>
                    <p className="text-sm text-center font-medium">
                      Integrated Module Design
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="md:py-10 pt-8">
          <div className="px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="md:text-center mb-8">
              <span className="text-white lg:text-4xl text-2xl font-semibold font-['Poppins'] md:leading-[57.60px]">
                Related{" "}
              </span>
              <span className="text-scenarios lg:text-4xl text-2xl font-semibold font-['Poppins'] md:leading-[57.60px]">
                Cases
              </span>
            </div>
            <div className="relative">
              <div className="overflow-hidden">
                <Swiper
                  slidesPerView={3}
                  spaceBetween={30}
                  navigation={true}
                  breakpoints={{
                    320: {
                      slidesPerView: 1,
                      spaceBetween: 20,
                    },
                    768: {
                      slidesPerView: 2,
                      spaceBetween: 30,
                    },
                    1024: {
                      slidesPerView: 3,
                      spaceBetween: 30,
                    },
                  }}
                  modules={[Navigation]}
                  className="related-cases-swiper mySwiper"
                >
                  {relatedCases.map((item) => (
                    <SwiperSlide>
                      <div key={item.id} className="text-center">
                        <img
                          alt={item.altText}
                          className="w-full rounded-sm mb-6"
                          src={item.imageUrl}
                        />
                        <button className="bg-[#215074] text-white px-5 py-2 rounded-lg border border-[#1c4e74] backdrop-blur-lg">
                          {item.buttonText}
                        </button>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </div>
        </div>
        <div className="md:pt-10 md:pb-20 py-8">
          <div className="px-6 lg:px-8 max-w-7xl mx-auto text-white inertia-ps">
            <div className="text-center md:mb-8 mb-4">
              <span class="text-white md:text-4xl text-2xl font-semibold font-['Poppins'] leading-[57.60px]">
                Product{" "}
              </span>
              <span class="text-scenarios md:text-4xl text-2xl font-semibold font-['Poppins'] leading-[57.60px]">
                Specifications
              </span>
            </div>
            <div className="relative">
              <ProductSpecifications
                specs={inertiaLEDProductSpecs}
                gridClasses="xl:grid-cols-4 text-center"
                hiddenHead="header-hide"
              />
              <img
                className="absolute hidden md:block lg:bottom-[-150px] h-[800px] left-[25%] right-0 w-[800px] z-1"
                alt="shadow-video-wall"
                src="https://content.qeeb.in/coltec/products/active-led/shadow-video-wall.webp"
              />
            </div>
          </div>
        </div>
        <div className="w-full relative md:h-[156px] bg-gradient-to-l from-[#6d91ff] to-[#2eaccf]">
          <div className="px-6 py-8 sm:px-6 lg:py-14 lg:px-8 max-w-7xl mx-auto">
            <div className="flex md:flex-row flex-col justify-start items-start md:justify-between md:items-center">
              <h2 className="lg:text-[30px] text-[19px] font-medium leading-[1.3] text-white md:max-w-3xl">
                Upgrade to Next-Level Visual Brilliance
              </h2>
              <div className="flex items-center justify-center gap-x-6 mt-4 md:mt-0">
                <button
                  onClick={openModal}
                  className="flex items-center rounded-md bg-white lg:px-6 px-6 py-2 text-xs md:text-sm font-medium text-[#0A78B9] shadow-sm hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                  Get a Quote Now
                  <img
                    alt="right-arrow"
                    src="https://content.qeeb.in/coltec/blue-arrow.svg"
                    className="h-2 pl-2"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <ScrollToTopButton />
        <WhatsAppMsg />
        {/* Modal popup */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-6">
              <div
                className="fixed inset-0 transition-opacity"
                onClick={closeModal}
              >
                <div className="absolute inset-0 bg-black opacity-50"></div>
              </div>
              <div className="bg-[#f1f1f1] rounded-lg overflow-hidden shadow-xl transform transition-all max-w-[770px] w-full z-10">
                <div className="flex items-center justify-between p-4 bg-white">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Contact Form
                  </h3>
                  <div>
                    <button onClick={closeModal}>
                      <img
                        alt="close-icon"
                        className="w-6"
                        src="https://content.qeeb.in/coltec/close-icon.svg"
                      />
                    </button>
                  </div>
                </div>
                <div className="modal-iframe">
                  <iframe
                    src="/modal-form.html"
                    title="outer-iframe"
                    className="w-full h-full search-iframe"
                    style={{ border: "none" }}
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Inertia;
